/**
 * <media-zoom> — PDP product photo lightbox (snippets/pdp--media-zoom.liquid).
 *
 * Opens from the gallery's zoom button or a tap/click on the main photo, using a native
 * <dialog> (focus trap, Esc, inert page) and a Swiper with the Zoom, Keyboard and A11y
 * modules (pinch, double-tap, pan, arrows). Closing leaves the gallery on the last photo viewed.
 *
 * Analytics go through Shopify.analytics.publish (see assets/header-analytics.js for why
 * dataLayer is not used); a Custom Pixel can forward `ps_pdp:image_zoom` to GA4.
 */
class MediaZoom extends HTMLElement {
  static ZOOM_STEP = 1.5;
  static MAX_RATIO = 4;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.count = this.querySelector('.media-zoom__count');
    this.zoomIn = this.querySelector('[data-zoom-in]');
    this.zoomOut = this.querySelector('[data-zoom-out]');
    this.zoomReset = this.querySelector('[data-zoom-reset]');
    this.galleryEl = document.getElementById(this.dataset.gallery);
    this.trigger = this.galleryEl?.closest('.product-media-gallery__main')?.querySelector('.product-media-gallery__zoom');

    this.trigger?.addEventListener('click', () => this.open());
    this.querySelector('[data-zoom-close]').addEventListener('click', () => this.dialog.close());
    this.zoomIn.addEventListener('click', () => this.setRatio(this.ratio() * MediaZoom.ZOOM_STEP));
    this.zoomOut.addEventListener('click', () => this.setRatio(this.ratio() / MediaZoom.ZOOM_STEP));
    this.zoomReset.addEventListener('click', () => this.swiper.zoom.out());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close(); // the backdrop
    });
    this.dialog.addEventListener('close', () => this.onClose());

    // A plain listener, so it doesn't depend on when the gallery Swiper is created. Swiper sets
    // allowClick to false after a drag, so swiping the gallery won't open the dialog.
    this.galleryEl?.addEventListener('click', (event) => {
      if (!event.target.closest('img') || this.galleryEl.swiper?.allowClick === false) return;
      this.open();
    });
  }

  open() {
    const index = this.galleryEl?.swiper?.activeIndex || 0;
    if (!this.swiper) this.initSwiper(index);
    else this.swiper.slideTo(index, 0);

    this.dialog.showModal();
    document.body.classList.add('overflow-hidden');
    this.swiper.update(); // sizes were unknown while the dialog was closed
    this.updateControls();
    this.publish('open');
  }

  initSwiper(index) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.swiper = new Swiper(this.querySelector('.media-zoom__slides'), {
      initialSlide: index,
      speed: reduceMotion ? 0 : 300,
      // toggle: false turns off Swiper's double-tap toggle; a click/tap steps the zoom instead (below).
      zoom: { maxRatio: MediaZoom.MAX_RATIO, limitToOriginalSize: true, toggle: false },
      keyboard: { enabled: true },
      a11y: { enabled: true },
      navigation: {
        prevEl: this.querySelector('.media-zoom__prev'),
        nextEl: this.querySelector('.media-zoom__next'),
      },
      on: {
        // Each click on the photo zooms in one step, like the + button; at full zoom it resets.
        // Swiper only fires `click` when the pointer didn't move, so panning doesn't count.
        click: (swiper, event) => {
          if (!event.target.closest('.swiper-zoom-container')) return;
          this.setRatio(this.ratio() >= MediaZoom.MAX_RATIO ? 1 : this.ratio() * MediaZoom.ZOOM_STEP);
        },
        slideChange: () => this.updateControls(),
        zoomChange: (swiper, scale) => {
          if (scale > 1 && this.ratio() <= 1) this.publish('zoom');
          this.updateControls(scale);
        },
      },
    });
  }

  ratio() {
    return this.swiper?.zoom.scale || 1;
  }

  setRatio(ratio) {
    const next = Math.min(MediaZoom.MAX_RATIO, ratio);
    if (next <= 1) this.swiper.zoom.out();
    else this.swiper.zoom.in(next);
  }

  updateControls(scale = this.ratio()) {
    this.count.textContent = `${this.swiper.realIndex + 1} ${this.count.dataset.of} ${this.swiper.slides.length}`;
    this.zoomOut.disabled = this.zoomReset.disabled = scale <= 1;
    this.zoomIn.disabled = scale >= MediaZoom.MAX_RATIO;
    this.dialog.classList.toggle('is-max-zoom', scale >= MediaZoom.MAX_RATIO);
  }

  onClose() {
    this.swiper.zoom.out();
    document.body.classList.remove('overflow-hidden');
    this.galleryEl?.swiper?.slideTo(this.swiper.realIndex); // thumbs follow via the Thumbs module
    this.trigger?.focus({ preventScroll: true });
  }

  publish(action) {
    window.Shopify?.analytics?.publish?.('ps_pdp:image_zoom', {
      action,
      image: (this.swiper?.realIndex || 0) + 1,
    });
  }
}

if (!customElements.get('media-zoom')) customElements.define('media-zoom', MediaZoom);
